import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { extractMentionIds } from 'utils/mentions';
import { getMediaType, getHierarchyWarning } from 'utils/helpers';
import { useCreateTaskMutation } from 'service/project/hooks/useCreateTaskMutation';
import { useDeleteWorkItemMutation } from 'service/project/api/workItem';
import { useMediaUpload, useDeleteMedia } from 'service/project/hooks/useMediaUpload';
import { useTaskDetailsQuery } from 'service/project/hooks/workItem';
import { useTaskDetailManagement } from 'hooks/useTaskDetailManagement';
import { useWorkLogs } from 'service/project/hooks/useWorkLog';
import { useProjectQuery, useProjectDefaultQuery } from 'service/project';
import { useProjectSprintsQuery } from 'service/project/hooks/useSprintQueries';
import { WorkType, WorkTypeCategory } from 'types/project/projectDefaults';

type UiPriority = "low" | "medium" | "high";

/** What onSuccessSave is told about the item just saved. */
export interface SavedWorkItem {
  workItemUuid?: string;
  /** A new Epic in a Sprint project: it is shown on the Epics tab, never in the Backlog. */
  isNewEpic: boolean;
}

interface UseTaskFormProps {
  projectId: string;
  taskId: string | null;
  projectKey?: string;
  isCreateMode: boolean;
  initialStatusUuid?: string;
  authUser: any;
  onSuccessSave?: (saved: SavedWorkItem) => void;
  isOpen?: boolean; // For modals
}

export const useTaskForm = ({
  projectId,
  taskId,
  projectKey,
  isCreateMode,
  initialStatusUuid: propInitialStatusUuid,
  authUser,
  onSuccessSave,
  isOpen = true
}: UseTaskFormProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // const { toBacklog } = usePmoNavigation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialStatusUuid = propInitialStatusUuid || queryParams.get('status') || undefined;
  // Set when created from a sprint's list; without it a new item lands in the Backlog.
  const createInSprintUuid = isCreateMode ? queryParams.get('sprint') || undefined : undefined;
  // Only the Sprint page's main Create Work Item button lets the user choose where the item goes.
  const canPickDestination = isCreateMode && queryParams.get('pickSprint') === '1';
  // Preselects a work type by category, e.g. `type=EPIC` from the Epics tab.
  const initialTypeCategory = isCreateMode ? queryParams.get('type') : null;
  // Presets the parent, e.g. `parent=<uuid>` from an Epic's Add child.
  const initialParentUuid = isCreateMode ? queryParams.get('parent') : null;
  const isEditMode = !isCreateMode;

  // Child Items Filters
  const lastLoadedKeyRef = useRef<string | null>(null);

  const CHILD_FILTER_STORAGE_KEY = (key: string) => `pmo_child_filters_${key}`;

  const loadChildFiltersFromStorage = (key: string) => {
    try {
      const raw = sessionStorage.getItem(CHILD_FILTER_STORAGE_KEY(key));
      if (raw) {
        const parsed = JSON.parse(raw);
        parsed.hideCompleted = localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true';
        return parsed;
      }
    } catch (_) {}
    return {
      statuses: [],
      assignees: null,
      priorities: [],
      reporters: null,
      taskTypes: [],
      createdDateRange: { from: null, to: null },
      dueDateRange: { from: null, to: null },
      hideCompleted: key ? localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true' : false,
      sortBy: 'listOrder',
      sortDirection: 'ASC',
    };
  };

  const saveChildFiltersToStorage = (key: string, filters: any) => {
    try {
      sessionStorage.setItem(CHILD_FILTER_STORAGE_KEY(key), JSON.stringify(filters));
    } catch (_) {}
  };

  const [childFilters, setChildFilters] = useState<any>(() => {
    const key = taskId || projectKey;
    const defaults = {
      statuses: [],
      assignees: null,
      priorities: [],
      reporters: null,
      taskTypes: [],
      createdDateRange: { from: null, to: null },
      dueDateRange: { from: null, to: null },
      hideCompleted: false,
      sortBy: 'listOrder',
      sortDirection: 'ASC',
    };
    if (key) {
      const restored = loadChildFiltersFromStorage(key);
      return {
        ...restored,
        hideCompleted: localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true',
        _key: key,
      };
    }
    return {
      ...defaults,
      _key: null,
    };
  });

  // Reset/Load task-specific settings
  useEffect(() => {
    const key = taskId || projectKey;
    if (key && childFilters._key !== key) {
      setChildFilters(() => {
        const restored = loadChildFiltersFromStorage(key);
        return {
          ...restored,
          hideCompleted: localStorage.getItem(`pmo_hide_completed_children_${key}`) === 'true',
          _key: key,
        };
      });
    }
  }, [taskId, projectKey, childFilters._key]);

  // Persist preference per task/project key
  useEffect(() => {
    const key = taskId || projectKey;
    if (key && childFilters._key === key) {
      const { _key, ...filtersToSave } = childFilters;
      saveChildFiltersToStorage(key, filtersToSave);
      localStorage.setItem(`pmo_hide_completed_children_${key}`, String(childFilters.hideCompleted));
    }
  }, [childFilters, taskId, projectKey]);

  // Core Management Hook
  const detailManagement = useTaskDetailManagement({
    projectId,
    taskId,
    isEditMode: !isCreateMode,
    enabled: !!projectId && isOpen,
    filters: childFilters,
  });

  const {
    existingTask,
    isFetchingTask,
    childItems,
    linkedItems,
    attachments,
    setLocalAttachments,
    // setLocalLinkedItems,
    resetLocalState,
  } = detailManagement;

  // UI State
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<{ description: string; priority: UiPriority; labels: string[] }>({
    description: '', priority: 'medium', labels: []
  });
  const [newTaskTypeUuid, setNewTaskTypeUuid] = useState<string | null>(null);
  const [statusUuid, setStatusUuid] = useState<string | null>(null);
  const [assigneeUuid, setAssigneeUuid] = useState<string | null>(null);
  const [reporterUuid, setReporterUuid] = useState<string | null>(authUser?.appUserUuid || null);
  const [parentUuid, setParentUuid] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [isDueDateDirty, setIsDueDateDirty] = useState(false);
  const [storyPoints, setStoryPoints] = useState<number | null>(null);
  // null is the Backlog. Starts as the sprint whose list the item was created from.
  const [destinationSprintUuid, setDestinationSprintUuid] = useState<string | null>(createInSprintUuid ?? null);

  const [activeTab, setActiveTab] = useState<'comments' | 'history' | 'work-log'>(() => {
    if (location.state?.activeTab) return location.state.activeTab as any;
    return 'comments';
  });
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});
  const markDirty = (field: string) => setDirtyFields(prev => ({ ...prev, [field]: true }));

  // Queries
  const { data: project, isLoading: isProjectLoading } = useProjectQuery(projectId);
  const { data: defaultData, isLoading: isDefaultDataLoading } = useProjectDefaultQuery(projectId);
  const { data: logs = [] } = useWorkLogs(projectId, taskId || "");
  // A new item in a Sprint project can go to the Backlog or any planned sprint.
  const { data: sprintList } = useProjectSprintsQuery(
    isCreateMode && project?.boardType === 'SPRINT' ? projectId : undefined
  );
  const plannedSprints = useMemo(
    () => (sprintList?.sprints ?? []).filter((s) => s.status === 'PLANNING'),
    [sprintList]
  );
  const sprintUuidToCreateIn = isCreateMode ? destinationSprintUuid : undefined;

  const parentTaskId = parentUuid || (existingTask as any)?.workItemParentWorkItemUuid || (existingTask as any)?.parentWorkItemUuid;
  const { data: parentTask } = useTaskDetailsQuery(
    projectId, parentTaskId ?? undefined, 0, 20, false, 
    { enabled: !!parentTaskId && isOpen }
  );

  const grandParentTaskId = (parentTask as any)?.workItemParentWorkItemUuid;
  const { data: grandParentTask } = useTaskDetailsQuery(
    projectId, grandParentTaskId ?? undefined, 0, 0, false,
    { enabled: !!grandParentTaskId && isOpen }
  );

  // Deep linking logic
  const highlightAndScroll = useCallback((elementId: string, errorMsg: string) => {
    const startTime = Date.now();
    const timeoutMs = 6000; // Try for up to 6 seconds to let replies fetch
    const intervalMs = 100;
    let timerId: any = null;

    const checkElement = () => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('comment-highlight');
        setTimeout(() => element.classList.remove('comment-highlight'), 3000);
        navigate(location.pathname, { replace: true });
      } else if (Date.now() - startTime < timeoutMs) {
        timerId = setTimeout(checkElement, intervalMs);
      } else {
        toast.error(errorMsg);
        navigate(location.pathname, { replace: true });
      }
    };

    timerId = setTimeout(checkElement, 200);
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (!existingTask || isFetchingTask || !isOpen) return;
    const hash = window.location.hash;
    if (hash.startsWith('#comment-')) {
      setActiveTab('comments');
      const cleanCommentId = hash.replace('#comment-', '').split('_root-')[0];
      highlightAndScroll(`comment-${cleanCommentId}`, 'Comment not found');
    } else if (hash.startsWith('#worklog-')) {
      setActiveTab('work-log');
      highlightAndScroll(`worklog-${hash.replace('#worklog-', '')}`, 'Work log not found');
    }
  }, [existingTask, isFetchingTask, isOpen, highlightAndScroll]);

  // Hydration & Reset Logic
  const hydratedTaskIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      hydratedTaskIdRef.current = null;
      setIsHydrated(false);
      return;
    }

    // On page refresh in create mode, projectId starts as "" while useResolveProjectKey
    // is in-flight. Wait until we have a real projectId AND defaultData before hydrating
    // so we don't hit the else-branch and incorrectly set newTaskTypeUuid = null.
    if (isCreateMode && (!projectId || !defaultData)) return;

    const taskIdChanged = taskId !== hydratedTaskIdRef.current;
    if (taskIdChanged || (isCreateMode && !isHydrated)) {
      setIsHydrated(false);
      if (!taskId) hydratedTaskIdRef.current = null;
      
      // Reset core fields
      setTitle('');
      setFormData({ description: '', priority: 'medium', labels: [] });
      setDirtyFields({});
      setIsDueDateDirty(false);
      resetLocalState();
      setAssigneeUuid(null);
      setParentUuid(initialParentUuid);
      setStoryPoints(null);
      setDestinationSprintUuid(createInSprintUuid ?? null);

      // Apply Defaults or Initial Props
      let initialStatus = initialStatusUuid;
      if (!initialStatus && isCreateMode && defaultData?.workStatusDTOList) {
        initialStatus = defaultData.workStatusDTOList.find((s: any) => s.workStatusCategory === 'TODO')?.workStatusUuid;
      }
      setStatusUuid(initialStatus || null);
      
      // Default Task Type — safe to run now because we know defaultData is loaded
      if (isCreateMode && defaultData?.workTypeDTOList) {
        const defaultType =
          (initialTypeCategory &&
            defaultData.workTypeDTOList.find((t: WorkType) => t.workTypeCategory === initialTypeCategory)) ||
          defaultData.workTypeDTOList.find(
            (t: WorkType) => t.workTypeCategory === 'TASK' || t.name?.toLowerCase() === 'task'
          ) ||
          defaultData.workTypeDTOList[0];
        setNewTaskTypeUuid(defaultType?.workTypeUuid || null);
      } else {
        setNewTaskTypeUuid(null);
      }

      setReporterUuid(authUser?.appUserUuid || null);
      
      if (isCreateMode) {
        setIsHydrated(true);
        hydratedTaskIdRef.current = taskId;
      }
    }
  }, [projectId, taskId, isOpen, initialStatusUuid, initialTypeCategory, initialParentUuid, createInSprintUuid, isCreateMode, isHydrated, defaultData, authUser?.appUserUuid, resetLocalState]);

  useEffect(() => {
    if (isOpen && !isCreateMode && existingTask && taskId && taskId !== hydratedTaskIdRef.current) {
      const task = existingTask as any;
      setTitle(task.title || task.workItemTitle || '');
      setFormData({
        description: task.description || task.workItemDescription || '',
        priority: (task.workPriority?.toLowerCase() as UiPriority) || 'medium',
        labels: task.projectLabelUuidList?.map((l: any) => typeof l === 'string' ? l : l?.projectLabelUuid || l?.uuid || String(l)) || [],
      });
      setNewTaskTypeUuid(task.workTypeUuid || null);
      setStatusUuid(task.workStatusUuid || null);
      setAssigneeUuid(task.workItemAssigneeAppUserUuid || null);
      setReporterUuid(task.workItemReporterAppUserUuid || authUser?.appUserUuid || null);
      setParentUuid(task.workItemParentWorkItemUuid || task.parentWorkItemUuid || null);
      setDueDate(task.dueDate || task.workItemDueDate || null);
      setStoryPoints(task.storyPoints ?? null);
      setIsDueDateDirty(false);
      setDirtyFields({});
      hydratedTaskIdRef.current = taskId;
      setIsHydrated(true);
    }
  }, [existingTask, isOpen, isCreateMode, taskId, authUser?.appUserUuid]);

  // Hierarchy Logic
  const currentCategory = useMemo(() => {
    if (!defaultData?.workTypeDTOList || !newTaskTypeUuid) return 'TASK';
    const type = defaultData.workTypeDTOList.find((t: WorkType) => t.workTypeUuid === newTaskTypeUuid);
    return type ? type.workTypeCategory : 'TASK';
  }, [defaultData, newTaskTypeUuid]);

  // The backend only takes points on stories in Sprint (Scrum) projects.
  const isSprintProject = project?.boardType === 'SPRINT';
  const canEstimate = isSprintProject && currentCategory === 'STORY';
  // Epics aren't planned in sprints: in a Sprint project they're only created from the main
  // Create Work Item button or the Epics tab, never from a sprint's or the Backlog's list.
  // An existing item is always in the Backlog or a sprint there, so it can't become one.
  const hideEpicType =
    isSprintProject && (isEditMode || (!canPickDestination && initialTypeCategory !== 'EPIC'));

  // Changing a story to another type clears its points on the backend; match that here.
  useEffect(() => {
    if (defaultData && newTaskTypeUuid && currentCategory !== 'STORY') setStoryPoints(null);
  }, [defaultData, newTaskTypeUuid, currentCategory]);

  const disabledTaskTypeOptions = useMemo(() => {
    if (!defaultData?.workTypeDTOList || childItems.length === 0) return {};
    const mapping: Record<string, string> = {};
    const childCategories = childItems.map((c: any) => c.workTypeCategory as WorkTypeCategory).filter(Boolean);
    defaultData.workTypeDTOList.forEach((type: WorkType) => {
      if (getHierarchyWarning(type.workTypeCategory, childCategories)) {
        mapping[type.workTypeUuid] = "This type can’t be selected because some child items aren’t supported under it.";
      }
    });
    return mapping;
  }, [defaultData, childItems]);

  // Mutations
  const { mutate: saveTask, mutateAsync: saveTaskAsync, isPending: isSaving } = useCreateTaskMutation();
  const { mutateAsync: uploadMediaAsync } = useMediaUpload();
  const { mutate: deleteMedia } = useDeleteMedia();
  const { mutate: deleteTask, isPending: isDeleting } = useDeleteWorkItemMutation();

  const handleFilesSelect = useCallback(async (files: File[]) => {
    if (!projectId) return;
    const newAttachments = files.map(file => {
      const localId = Math.random().toString(36).substring(7);
      return { id: localId, name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file), file, status: 'uploading' as const };
    });
    setLocalAttachments((prev: any[]) => [...prev, ...newAttachments]);

    const uploadPromises = newAttachments.map(async (attachment) => {
      try {
        const response = await uploadMediaAsync({
          file: attachment.file, projectUuid: projectId, workItemUuid: taskId || "", 
          originalFileName: attachment.name, mediaType: getMediaType(attachment.file)
        });
        setLocalAttachments((prev: any[]) => prev.map((a: any) => a.id === attachment.id ? { ...a, status: 'ready', mediaUuid: response.mediaUuid } : a));
        return response;
      } catch (error: any) {
        setLocalAttachments((prev: any[]) => prev.map((a: any) => a.id === attachment.id ? { ...a, status: 'error', errorMessage: error.message || 'Upload failed' } : a));
        throw error;
      }
    });
    await Promise.allSettled(uploadPromises);
    if (taskId) queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] });
  }, [projectId, taskId, uploadMediaAsync, setLocalAttachments, queryClient]);

  const handleEditorImageUpload = useCallback(async (file: File): Promise<string> => {
    if (!projectId) throw new Error("Project ID is required");
    const localId = Math.random().toString(36).substring(7);
    setLocalAttachments((prev: any[]) => [...prev, { id: localId, name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file), file, status: "uploading" }]);
    try {
      const response = await uploadMediaAsync({
        file, projectUuid: projectId, workItemUuid: taskId || "", originalFileName: file.name, mediaType: getMediaType(file)
      });
      if (!response.status || !response.url) throw new Error(response.message || "Failed to upload image");
      setLocalAttachments((prev: any[]) => prev.map((a: any) => a.id === localId ? { ...a, status: "ready", mediaUuid: response.mediaUuid } : a));
      if (taskId) queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] });
      return response.url.split("?")[0];
    } catch (error: any) {
      setLocalAttachments((prev: any[]) => prev.map((a: any) => a.id === localId ? { ...a, status: "error", errorMessage: error.message || "Upload failed" } : a));
      throw error;
    }
  }, [projectId, taskId, uploadMediaAsync, setLocalAttachments, queryClient]);

  const handleRemoveAttachment = (id: string) => {
    const attachment = attachments.find((a: any) => a.id === id);
    if (attachment?.mediaUuid && projectId) {
      deleteMedia({ projectUuid: projectId, mediaUuid: attachment.mediaUuid, workItemUuid: taskId || "" }, {
        onSuccess: () => { if (taskId) queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] }); }
      });
    }
    setLocalAttachments((prev: any[]) => prev.filter((a: any) => a.id !== id));
  };

  const PRIORITY_MAP: Record<UiPriority, string> = { high: "HIGH", medium: "MEDIUM", low: "LOW" };

  const handleTask = (options?: { skipNavigation?: boolean, onSuccessCallback?: () => void }) => {
    if (!title.trim()) { setTitleError(true); return; }
    setTitleError(false);
    if (!projectId || !newTaskTypeUuid || !statusUuid || isSaving) return;

    const payload: any = {
      ...(isEditMode && { workItemUuid: taskId }),
      projectUuid: projectId,
      workItemTitle: title.trim(),
      ...(isEditMode ? (newTaskTypeUuid !== (existingTask as any)?.workTypeUuid ? { workTypeUuid: newTaskTypeUuid } : {}) : { workTypeUuid: newTaskTypeUuid }),
      ...(isEditMode ? (statusUuid !== (existingTask as any)?.workStatusUuid ? { workStatusUuid: statusUuid } : {}) : { workStatusUuid: statusUuid }),
      ...(isEditMode ? (reporterUuid !== (existingTask as any)?.workItemReporterAppUserUuid ? { workItemReporterAppUserUuid: reporterUuid || "" } : {}) : { workItemReporterAppUserUuid: reporterUuid || "" }),
      ...(isEditMode ? (assigneeUuid !== (existingTask as any)?.workItemAssigneeAppUserUuid ? { workItemAssigneeAppUserUuid: assigneeUuid } : {}) : { workItemAssigneeAppUserUuid: assigneeUuid }),
      ...(isEditMode ? (PRIORITY_MAP[formData.priority] !== (existingTask as any)?.workPriority ? { workPriority: PRIORITY_MAP[formData.priority] } : {}) : { workPriority: PRIORITY_MAP[formData.priority] || "MEDIUM" }),
      workItemDescription: formData.description,
      projectLabelUuidList: formData.labels,
      projectRemoveLabelUuidList: (existingTask?.projectLabelUuidList?.map((l: any) => typeof l === "string" ? l : l?.projectLabelUuid || l?.uuid || String(l)) || []).filter((id: string) => !formData.labels.includes(id)),
      mentionedAppUsersUuidList: extractMentionIds(formData.description),
      ...(isEditMode ? (parentUuid !== (existingTask as any)?.parentWorkItemUuid ? {
        workItemParentWorkItemUuid: parentUuid || "",
        parentWorkItemUuid: parentUuid || ""
      } : {}) : {
        workItemParentWorkItemUuid: parentUuid || undefined,
        parentWorkItemUuid: parentUuid || undefined,
      }),
      workItemMediaUuidList: attachments.filter((a: any) => a.status === "ready" && a.mediaUuid).map((a: any) => a.mediaUuid),
      ...((!isEditMode && !!dueDate) || isDueDateDirty ? { workItemDueDate: dueDate } : {}),
      // Omitted otherwise: leaving sprintUuid out keeps the item's sprint as it is.
      // Never sent for an Epic: a sprint picked before switching to Epic is only hidden, not cleared.
      ...(sprintUuidToCreateIn && currentCategory !== 'EPIC' ? { sprintUuid: sprintUuidToCreateIn } : {}),
      // Edits save points on their own (see handleUpdateTask), so only a create sends them here.
      ...(!isEditMode && canEstimate && storyPoints !== null ? { storyPoints } : {}),
    };

    if (!isEditMode) {
      payload.workItemAddLinkDTOList = linkedItems.map((link: any) => ({ targetWorkItemUuid: link.targetWorkItemUuid }));
    } else {
      payload.workItemLinkReqDTOList = linkedItems.map((link: any) => ({ sourceWorkItemUuid: taskId || '', targetWorkItemUuid: link.targetWorkItemUuid }));
      payload.workItemDTOList = childItems.map((child: any) => ({
        ...(child.id.includes("-") && { workItemUuid: child.id }),
        workItemTitle: child.title,
        workTypeUuid: child.workTypeUuid || newTaskTypeUuid,
        workItemReporterAppUserUuid: authUser?.appUserUuid || null,
        workItemAssigneeAppUserUuid: child.assigneeUuid,
        workPriority: PRIORITY_MAP[child.priority as UiPriority] || "MEDIUM",
      }));
    }

    saveTask(payload, {
      onSuccess: (data) => {
        setDirtyFields({}); setIsDueDateDirty(false); setIsHydrated(false); hydratedTaskIdRef.current = null;
        queryClient.invalidateQueries({ queryKey: ["tasks"], exact: false });
        if (isEditMode) {
          queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] });
          queryClient.invalidateQueries({ queryKey: ["task-history", projectId, taskId] });
          setIsEditingDescription(false);
          setIsSavingDescription(false);
        }
        if (options?.onSuccessCallback) {
          options.onSuccessCallback();
        } else if (onSuccessSave && !options?.skipNavigation) {
          onSuccessSave({
            workItemUuid: data?.workItemUuid,
            isNewEpic: !isEditMode && isSprintProject && currentCategory === 'EPIC',
          });
        }
      },
      onError: (err) => console.error("Failed to save task:", err)
    });
  };

  const handleUpdateTask = (
    overrides: any,
    options?: { onSuccess?: () => void; onError?: (err: any) => void }
  ) => {
    if (!isEditMode || !taskId) return;

    const isParentChanging = overrides.workItemParentWorkItemUuid !== undefined || overrides.parentWorkItemUuid !== undefined;
    const oldParentUuid = (existingTask as any)?.workItemParentWorkItemUuid || (existingTask as any)?.parentWorkItemUuid || parentUuid || null;
    const newParentUuid = isParentChanging
      ? (overrides.workItemParentWorkItemUuid !== undefined ? overrides.workItemParentWorkItemUuid : overrides.parentWorkItemUuid)
      : oldParentUuid;

    const payload: any = {
      workItemUuid: taskId,
      projectUuid: projectId!,
      workItemTitle: overrides.workItemTitle !== undefined ? overrides.workItemTitle : (existingTask?.title || (existingTask as any)?.workItemTitle || title.trim()),
      ...(overrides.workTypeUuid !== undefined && { workTypeUuid: overrides.workTypeUuid }),
      workStatusUuid: overrides.workStatusUuid !== undefined ? overrides.workStatusUuid : ((existingTask as any)?.workStatusUuid || statusUuid),
      workItemReporterAppUserUuid: overrides.workItemReporterAppUserUuid !== undefined ? overrides.workItemReporterAppUserUuid : ((existingTask as any)?.workItemReporterAppUserUuid || reporterUuid),
      workItemAssigneeAppUserUuid: overrides.workItemAssigneeAppUserUuid !== undefined ? overrides.workItemAssigneeAppUserUuid : ((existingTask as any)?.workItemAssigneeAppUserUuid || assigneeUuid),
      workPriority: overrides.workPriority !== undefined ? overrides.workPriority : ((existingTask as any)?.workPriority || PRIORITY_MAP[formData.priority]),
      workItemDescription: overrides.workItemDescription !== undefined ? overrides.workItemDescription : (existingTask?.description || (existingTask as any)?.workItemDescription || ""),
      projectLabelUuidList: overrides.projectLabelUuidList !== undefined ? overrides.projectLabelUuidList : ((existingTask as any)?.projectLabelUuidList || formData.labels),
      workItemParentWorkItemUuid: overrides.workItemParentWorkItemUuid !== undefined ? overrides.workItemParentWorkItemUuid : ((existingTask as any)?.workItemParentWorkItemUuid || (existingTask as any)?.parentWorkItemUuid || parentUuid || undefined),
      parentWorkItemUuid: overrides.parentWorkItemUuid !== undefined ? overrides.parentWorkItemUuid : ((existingTask as any)?.workItemParentWorkItemUuid || (existingTask as any)?.parentWorkItemUuid || parentUuid || undefined),
    };
    if (overrides.workItemDueDate !== undefined) payload.workItemDueDate = overrides.workItemDueDate;
    // null clears the points; left out, they stay as they are.
    if (overrides.storyPoints !== undefined) payload.storyPoints = overrides.storyPoints;

    // Always extract mentions from the description being saved so the backend
    // can fire MENTIONED_IN_TASK notifications on every description update.
    const descriptionToExtract = overrides.workItemDescription !== undefined
      ? overrides.workItemDescription
      : (existingTask?.description || (existingTask as any)?.workItemDescription || '');
    payload.mentionedAppUsersUuidList = extractMentionIds(descriptionToExtract);

    // Chained on the promise rather than passed to mutate: react-query runs mutate's
    // per-call callbacks only for the latest call and only while mounted, and the
    // caller's onSuccess/onError (rollbacks, the Epic-done prompt) must always run.
    saveTaskAsync(payload).then(
      () => {
        queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] });
        queryClient.invalidateQueries({ queryKey: ["task-history", projectId, taskId] });
        
        // Eagerly remove from infinite queries to prevent UI jumping before refetch completes
        if (isParentChanging && oldParentUuid && newParentUuid !== oldParentUuid) {
          const removeChildRecursively = (tasks: any[]): any[] => {
            if (!tasks) return tasks;
            return tasks.map(task => ({
              ...task,
              childWorkItems: task.childWorkItems 
                ? removeChildRecursively(task.childWorkItems.filter((c: any) => c.workItemUuid !== taskId && c.id !== taskId))
                : task.childWorkItems
            }));
          };

          queryClient.setQueriesData({ queryKey: ["tasks", "infinite"] }, (oldData: any) => {
            if (!oldData || !oldData.pages) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) => {
                const pageData = page.response?.[0] || page.response || page;
                if (!pageData?.data) return page;
                return { ...page, response: [{ ...pageData, data: removeChildRecursively(pageData.data) }] };
              })
            };
          });
        }

        queryClient.invalidateQueries({ queryKey: ["tasks"], exact: false });
        queryClient.invalidateQueries({ queryKey: ['task-children', projectId], exact: false });
        if (oldParentUuid) {
          queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, oldParentUuid], exact: false });
          queryClient.invalidateQueries({ queryKey: ['task-children', projectId, oldParentUuid], exact: false });
        }
        if (overrides && overrides.workItemDescription !== undefined) {
          setIsEditingDescription(false); setIsSavingDescription(false);
        }

        if (isParentChanging && newParentUuid !== oldParentUuid) {
          if (oldParentUuid) {
            queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, oldParentUuid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['task', projectId, oldParentUuid] });
          }
          if (newParentUuid) {
            queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, newParentUuid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['task', projectId, newParentUuid] });
          }
          toast.success("Parent work item updated successfully.");
        }

        options?.onSuccess?.();
      },
      (err: any) => {
        if (overrides && overrides.workItemDescription !== undefined) setIsSavingDescription(false);
        const errorMsg = err?.response?.data?.message || err?.message || 'Failed to save changes';
        toast.error(errorMsg);
        options?.onError?.(err);
      }
    );
  };

  const handleEditorChange = (content: string) => { setFormData((prev) => ({ ...prev, description: content })); markDirty("description"); };
  const handlePriorityChange = (priority: any) => { setFormData((prev) => ({ ...prev, priority: priority as UiPriority })); markDirty("priority"); };
  const handleLabelChange = (labels: string[]) => { setFormData((prev) => ({ ...prev, labels })); markDirty("labels"); };
  
  const handleContainerClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG' && (target as HTMLImageElement).src) {
      if (!target.closest('.prose') || target.closest('.tiptap') || target.classList.contains('no-preview')) return;
      setPreviewImage((target as HTMLImageElement).src);
    }
  };

  return {
    // UI State
    title, setTitle, titleError, setTitleError, isHydrated, formData, setFormData,
    newTaskTypeUuid, setNewTaskTypeUuid, statusUuid, setStatusUuid,
    assigneeUuid, setAssigneeUuid, reporterUuid, setReporterUuid,
    parentUuid, setParentUuid, dueDate, setDueDate, isDueDateDirty, setIsDueDateDirty,
    storyPoints, setStoryPoints, isSprintProject, canEstimate,
    canPickDestination, plannedSprints, destinationSprintUuid, setDestinationSprintUuid,
    activeTab, setActiveTab, isEditingDescription, setIsEditingDescription,
    isSavingDescription, setIsSavingDescription, previewImage, setPreviewImage,
    isCopied, setIsCopied, childFilters, setChildFilters,
    dirtyFields, markDirty,
    
    // Handlers
    handleEditorChange, handlePriorityChange, handleLabelChange, handleContainerClick,
    handleFilesSelect, handleEditorImageUpload, handleRemoveAttachment,
    handleTask, handleUpdateTask,
    
    // Management Data
    detailManagement, project, defaultData, logs, parentTask, grandParentTask,
    currentCategory, disabledTaskTypeOptions, hideEpicType,
    isSaving,
    isDeleting,
    isProjectLoading,
    isDefaultDataLoading,
    deleteTask
  };
};
